<script>
  import { onMount } from 'svelte';
  import Cookies from 'js-cookie';
  import {
    listenForChatMessages,
    listenForReactions,
    listenForChatDeleteMessages,
    retrieveChatHistory,
    retrievePinnedMessage,
    sendChatMessage,
    setChatName,
    setChatRateLimitError,
  } from '../js/actions.js';
  import { chatState, broadcasterState, streamState, accessState } from '../js/state.js';
  import Bulletin from './Bulletin.svelte';
  import ReactionControl from './ReactionControl.svelte';
  import ReactionDisplay from './ReactionDisplay.svelte';
  import CreateRoom from './rooms/CreateRoom.svelte';
  import SelectRoom from './rooms/SelectRoom.svelte';
  import TitleRoomSelect from './rooms/TitleRoomSelect.svelte';
  import RoomInvite from './rooms/RoomInvite.svelte';
  import VerifyPopup from './organizer/VerifyPopup.svelte';
  import {
    CHAT_NOTHING_NEW,
    CHAT_TYPE_A_MESSAGE,
    CHAT_YOUR_NAME,
    CHAT_START_CHATTING,
    CHAT_ENTER_YOUR_FULL_NAME,
    CHAT_SEEN_AS,
    CHAT_NO_NAME_CHANGE,
    CHAT_CONTINUE_AS,
    GO_BACK,
  } from '../js/l10n/en_US.js';
  import { CHAT_GENERAL_CHANNEL_NAME } from '../js/constants.js';
  import ChatMessage from './ChatMessage.svelte';

  export let message = '',
    fullName = '',
    token = '',
    source = '',
    id = '',
    room = CHAT_GENERAL_CHANNEL_NAME,
    showMessageField = false,
    showReactions = false,
    sub = '',
    history = [];

  let chatWindow,
    connected = false,
    showRoomCreate = false,
    showRoomSelect = false,
    roomsEnabled = false,
    nameConfirmed = false,
    allowSend = true,
    sentMessages = [], // tracks messages in rate limit window
    rateLimitPerMinute = 12,
    verifyPopup;

  $: roomsEnabled = $streamState && $streamState.use_rooms;

  function sendChat() {
    if (!fullName) {
      return;
    }

    let t = new Date();
    sentMessages.push(t);
    if (sentMessages.length >= rateLimitPerMinute) {
      let delay = 60000 - (t.getTime() - sentMessages[0].getTime());
      delay = Math.max(delay, 0);
      allowSend = false;
      setChatRateLimitError();
      setTimeout(function () {
        sentMessages = [];
        if (sentMessages.length < rateLimitPerMinute) {
          allowSend = true;
        }
      }, delay);
    }

    sendChatMessage(token, source, id, $chatState.name, message, room);
    message = '';
  }

  function setName() {
    verifyPopup.openVerifyPopup(
      '',
      '',
      () => {
        setChatName(fullName);
        if (fullName && message) sendChat();
      },
      () => {
        setChatName('');
      }
    );
  }

  function toggleCreateRoom() {
    showRoomCreate = !showRoomCreate;
    if (showRoomCreate) {
      showRoomSelect = false;
      setTimeout(() => {
        document.getElementById('room-name-input').focus();
      }, 500);
    } else {
      document.getElementById('room-name-input').blur();
    }
  }

  function toggleSelectRoom() {
    showRoomSelect = !showRoomSelect;
    if (showRoomSelect) {
      showRoomCreate = false;
    }
  }

  function scrollToTheBottom() {
    if (!chatWindow) {
      chatWindow = document.getElementsByClassName('chat-feed')[0];
    }
    if (chatWindow) {
      setTimeout(() => {
        chatWindow.scrollTop = chatWindow.scrollHeight;
      }, 500);
    }
  }

  function getCookieName(source, id) {
    return `chat-name-${source}-${id}`;
  }

  // Events
  onMount(() => {
    fullName = Cookies.get(getCookieName(source, id));
    setChatName(fullName);
  });

  $: if (token && source && id) {
    retrieveChatHistory(token, source, id);
    retrievePinnedMessage(token, source, id);
  }

  $: if ($broadcasterState.receiver && !connected) {
    listenForChatMessages();
    listenForChatDeleteMessages();
    listenForReactions();
    // get initial history
    connected = true;
  }

  // Reactive
  $: if (room && $broadcasterState.receiver) {
    const alreadySubscribed = $broadcasterState.receiver.getChannels().indexOf(room) != -1;
    if (!alreadySubscribed) {
      listenForChatMessages(room);
      listenForChatDeleteMessages(room);
    }
  }

  $: if ($chatState) {
    scrollToTheBottom();
  }

  $: if (room) {
    // load room history on room change
    retrieveChatHistory(token, source, id, room);
  }

  $: if ($chatState && $chatState.name) {
    Cookies.set(getCookieName(source, id), $chatState.name, {
      sameSite: 'lax',
    });
  }

  $: history = $chatState.history[room] || [];
</script>

<VerifyPopup
  bind:this={verifyPopup}
  forceInteraction={true}
  confirm={CHAT_CONTINUE_AS + ' ' + fullName}
  cancel={GO_BACK}
>
  <span>
    {CHAT_SEEN_AS}
    <strong>{fullName}</strong>
    . {CHAT_NO_NAME_CHANGE}
  </span>
</VerifyPopup>

<div class="chat">
  <div class="chat-header-wrap">
    <div class="chat-header">
      <span class="chat-header-section chat-header-title">
        <TitleRoomSelect {roomsEnabled} toggleVisible={toggleSelectRoom} />
      </span>

      <span class="chat-header-section chat-header-icons">
        {#if roomsEnabled && $accessState.token}
          <i
            class="icon-add"
            on:click={() => {
              toggleCreateRoom();
            }}
          />
        {/if}
      </span>
    </div>
  </div>

  {#if showRoomCreate}
    <div class="expand-container">
      <CreateRoom {source} eventId={id} token={$accessState.token} onClose={toggleCreateRoom} />
    </div>
  {/if}

  {#if showRoomSelect}
    <div class="expand-container">
      <SelectRoom {source} eventId={id} token={$accessState.token} onClose={toggleSelectRoom} />
    </div>
  {/if}

  {#if !$chatState}
    <em>{CHAT_NOTHING_NEW}</em>
  {:else}
    <Bulletin />
    {#if roomsEnabled}
      <RoomInvite />
    {/if}
    <ul class="chat-feed">
      {#each history as msg}
        {#if !msg.hide_chat}
          <ChatMessage
            {id}
            {source}
            isMe={msg.sub === sub}
            name={msg.name}
            time={msg.time}
            message={msg.message}
            isModerator={msg.moderator}
          />
        {/if}
      {/each}
    </ul>

    {#if !$chatState.name || ($chatState.name && $chatState.name == '')}
      <div>
        <form class="name-form" on:submit|preventDefault={setName} autocomplete="off">
          <label for="full-name-input">{CHAT_ENTER_YOUR_FULL_NAME}</label>
          <input
            id="full-name-input"
            bind:value={fullName}
            aria-label={CHAT_YOUR_NAME}
            placeholder={CHAT_YOUR_NAME}
          />
          <button type="submit" class="btn text-light">{CHAT_START_CHATTING}</button>
        </form>
      </div>
    {:else}
      <div>
        {#if showReactions && token}
          <ReactionControl {token} {source} eventId={id} />
        {/if}
        {#if showMessageField}
          <form class="chat-form" on:submit|preventDefault={sendChat} autocomplete="off">
            <input
              class="chat-message-text"
              bind:value={message}
              aria-label={CHAT_TYPE_A_MESSAGE}
              placeholder={CHAT_TYPE_A_MESSAGE}
            />
            <button disabled={!allowSend} class="submit clear" type="submit" alt="Submit">
              <i class="icon-send" />
            </button>
          </form>
        {/if}
      </div>
    {/if}
  {/if}

  {#if showReactions && token}
    <ReactionDisplay />
  {/if}
</div>
